import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import {
  Avatar, Button, IconButton, InputAdornment, Paper,
  Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Typography, Fab,
} from "@material-ui/core";
import { DeleteOutline, Edit, Add, ExtensionOutlined } from "@material-ui/icons";
import SearchIcon from "@material-ui/icons/Search";

import n8n from "../../assets/n8n.png";
import dialogflow from "../../assets/dialogflow.png";
import webhooks from "../../assets/webhook.png";
import typebot from "../../assets/typebot.jpg";
import flowbuilder from "../../assets/flowbuilders.png";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import IntegrationModal from "../../components/QueueIntegrationModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import ForbiddenPage from "../../components/ForbiddenPage";

const TYPE_ASSETS = {
  dialogflow,
  n8n,
  webhook: webhooks,
  typebot,
  flowbuilder,
};

const reducer = (state, action) => {
  if (action.type === "LOAD_INTEGRATIONS") {
    const list = action.payload;
    const newItems = [];
    list.forEach((item) => {
      const idx = state.findIndex((u) => u.id === item.id);
      if (idx !== -1) state[idx] = item; else newItems.push(item);
    });
    return [...state, ...newItems];
  }
  if (action.type === "UPDATE_INTEGRATIONS") {
    const item = action.payload;
    const idx = state.findIndex((u) => u.id === item.id);
    if (idx !== -1) { state[idx] = item; return [...state]; }
    return [item, ...state];
  }
  if (action.type === "DELETE_INTEGRATION") {
    const idx = state.findIndex((u) => u.id === action.payload);
    if (idx !== -1) state.splice(idx, 1);
    return [...state];
  }
  if (action.type === "RESET") return [];
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  avatar: {
    width: 100,
    height: 32,
    borderRadius: 4,
  },
  mobileList: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1),
  },
  card: {
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    padding: theme.spacing(1.5),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  cardLogo: {
    width: 72,
    height: 28,
    borderRadius: 4,
    objectFit: "contain",
    flexShrink: 0,
    background: theme.palette.action.hover,
  },
  cardIconFallback: {
    width: 42,
    height: 42,
    borderRadius: 10,
    background: "rgba(91,45,142,0.08)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    color: "#5B2D8E",
  },
  cardBody: { flex: 1, minWidth: 0 },
  cardName: {
    fontWeight: 600,
    fontSize: "0.95rem",
    color: theme.palette.text.primary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  cardType: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    textTransform: "capitalize",
  },
  cardActions: { display: "flex", flexShrink: 0, gap: 2 },
  mobileHeader: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    width: "100%",
    padding: theme.spacing(0, 0, 1),
  },
  mobileHeaderRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  fab: {
    position: "fixed",
    bottom: theme.spacing(3),
    right: theme.spacing(3),
    zIndex: 10,
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(6, 2),
    color: theme.palette.text.secondary,
    gap: theme.spacing(1),
  },
}));

const QueueIntegration = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [deletingIntegration, setDeletingIntegration] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [queueIntegration, dispatch] = useReducer(reducer, []);

  const { user, socket } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();
  const history = useHistory();

  useEffect(() => {
    async function fetchData() {
      try {
        const planConfigs = await getPlanCompany(undefined, user.companyId);
        if (!planConfigs?.plan?.useIntegrations) {
          toast.error("Esta empresa não possui permissão para acessar essa página!");
          setTimeout(() => history.push("/"), 1000);
        }
      } catch (err) {}
    }
    fetchData();
  }, []);

  useEffect(() => { dispatch({ type: "RESET" }); setPageNumber(1); }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/queueIntegration/", { params: { searchParam, pageNumber } });
        dispatch({ type: "LOAD_INTEGRATIONS", payload: data.queueIntegrations });
        setHasMore(data.hasMore);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    }, 500);
    return () => clearTimeout(t);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const onEvent = (data) => {
      if (data.action === "update" || data.action === "create")
        dispatch({ type: "UPDATE_INTEGRATIONS", payload: data.queueIntegration });
      if (data.action === "delete")
        dispatch({ type: "DELETE_INTEGRATION", payload: +data.integrationId });
    };
    socket.on(`company-${user.companyId}-queueIntegration`, onEvent);
    return () => socket.off(`company-${user.companyId}-queueIntegration`, onEvent);
  }, []);

  const handleOpenModal = () => { setSelectedIntegration(null); setModalOpen(true); };
  const handleCloseModal = () => { setSelectedIntegration(null); setModalOpen(false); };
  const handleSearch = (e) => setSearchParam(e.target.value.toLowerCase());
  const handleEdit = (item) => { setSelectedIntegration(item); setModalOpen(true); };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/queueIntegration/${id}`);
      toast.success(i18n.t("queueIntegration.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingIntegration(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) setPageNumber((p) => p + 1);
  };

  const renderLogo = (type) => {
    const src = TYPE_ASSETS[type];
    if (src) return <img src={src} alt={type} className={classes.cardLogo} />;
    return (
      <div className={classes.cardIconFallback}>
        <ExtensionOutlined fontSize="small" />
      </div>
    );
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={deletingIntegration && `${i18n.t("queueIntegration.confirmationModal.deleteTitle")} ${deletingIntegration.name}?`}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDelete(deletingIntegration.id)}
      >
        {i18n.t("queueIntegration.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <IntegrationModal
        open={modalOpen}
        onClose={handleCloseModal}
        aria-labelledby="form-dialog-title"
        integrationId={selectedIntegration && selectedIntegration.id}
      />

      {user.profile === "user" ? <ForbiddenPage /> : (
        <>
          {isMobile ? (
            <>
              <MainHeader>
                <div className={classes.mobileHeader}>
                  <div className={classes.mobileHeaderRow}>
                    <Title>{i18n.t("queueIntegration.title")}</Title>
                    <Typography variant="caption" color="textSecondary">
                      {queueIntegration.length} integraciones
                    </Typography>
                  </div>
                  <TextField
                    fullWidth size="small" variant="outlined"
                    placeholder={i18n.t("queueIntegration.searchPlaceholder")}
                    type="search" value={searchParam} onChange={handleSearch}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon style={{ color: "gray" }} fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>
              </MainHeader>

              <Paper className={classes.mainPaper} variant="outlined" onScroll={handleScroll}>
                {queueIntegration.length === 0 && !loading ? (
                  <div className={classes.emptyState}>
                    <ExtensionOutlined style={{ fontSize: 48, opacity: 0.3 }} />
                    <Typography variant="body2">Sin integraciones</Typography>
                  </div>
                ) : (
                  <div className={classes.mobileList}>
                    {queueIntegration.map((item) => (
                      <div key={item.id} className={classes.card}>
                        {renderLogo(item.type)}
                        <div className={classes.cardBody}>
                          <div className={classes.cardName}>{item.name}</div>
                          <div className={classes.cardType}>{item.type}</div>
                        </div>
                        <div className={classes.cardActions}>
                          <IconButton size="small" onClick={() => handleEdit(item)}>
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => { setConfirmModalOpen(true); setDeletingIntegration(item); }}>
                            <DeleteOutline fontSize="small" />
                          </IconButton>
                        </div>
                      </div>
                    ))}
                    {loading && (
                      <Typography variant="caption" color="textSecondary" style={{ textAlign: "center" }}>
                        Cargando...
                      </Typography>
                    )}
                  </div>
                )}
              </Paper>

              <Fab color="primary" className={classes.fab} onClick={handleOpenModal}>
                <Add />
              </Fab>
            </>
          ) : (
            <>
              <MainHeader>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", gap: 16 }}>
                  <Title>{i18n.t("queueIntegration.title")} ({queueIntegration.length})</Title>
                  <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                    <TextField
                      placeholder={i18n.t("queueIntegration.searchPlaceholder")}
                      type="search" value={searchParam} onChange={handleSearch}
                      size="small" variant="outlined"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon style={{ color: "gray" }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Button variant="contained" color="primary" onClick={handleOpenModal}>
                      {i18n.t("queueIntegration.buttons.add")}
                    </Button>
                  </div>
                </div>
              </MainHeader>

              <Paper className={classes.mainPaper} variant="outlined" onScroll={handleScroll}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{i18n.t("queueIntegration.table.id")}</TableCell>
                      <TableCell align="center">Tipo</TableCell>
                      <TableCell align="center">{i18n.t("queueIntegration.table.name")}</TableCell>
                      <TableCell align="center">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {queueIntegration.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{item.id}</TableCell>
                        <TableCell align="center">
                          {TYPE_ASSETS[item.type]
                            ? <Avatar src={TYPE_ASSETS[item.type]} className={classes.avatar} />
                            : item.type}
                        </TableCell>
                        <TableCell align="center">{item.name}</TableCell>
                        <TableCell align="center">
                          <IconButton size="small" onClick={() => handleEdit(item)}>
                            <Edit color="secondary" />
                          </IconButton>
                          <IconButton size="small" onClick={() => { setConfirmModalOpen(true); setDeletingIntegration(item); }}>
                            <DeleteOutline color="secondary" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {loading && <TableRowSkeleton columns={4} />}
                  </TableBody>
                </Table>
              </Paper>
            </>
          )}
        </>
      )}
    </MainContainer>
  );
};

export default QueueIntegration;
